import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from "react";
import { ButtonGroup, Col, FormLabel, InputGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReactSlidingPane from 'react-sliding-pane';
import { setLang } from '../../../actions/config.actions';
import useBreakpoint from '../../hooks/useBreakpoint';
import Button from '../Button';
import ResponsiveHeader from '../ResponsiveHeader';

const LANGS = [
  { code: 'en', text: 'common.header.english' },
  { code: 'fr', text: 'common.header.french' },
  { code: 'es', text: 'common.header.spanish' },
  { code: 'de', text: 'common.header.german' },
  { code: 'ja', text: 'common.header.japanese' },
  { code: 'pt', text: 'common.header.portuguese' },
  { code: 'it', text: 'common.header.italian' },
  { code: 'ar', text: 'common.header.arabic' },
  { code: 'hi', text: 'common.header.hindi' },
];

const LanguageSelectorSlideOver = ({
  availableLanguages = ['en', 'fr', 'es', 'de', 'ja', 'pt', 'it', 'ar', 'hi']
}) => {
  const { t, i18n } = useTranslation();
  const globalLang = useSelector(store => store.config.lang);
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();

  const [isOpen, setIsOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      lang: LANGS.find(lang => lang.code === globalLang).code || 'en'
    },
    onSubmit: (values) => {
      if (values.lang) dispatch(setLang(values.lang));
      setIsOpen(false);
    },
    enableReinitialize: true
  });

  useEffect(() => {
    i18n.changeLanguage(globalLang);
    document.body.dir = i18n.dir();
  }, [globalLang]);

  const renderLangsItems = useCallback(() =>
    LANGS.map(lang => {
      if (!availableLanguages.includes(lang.code)) return;

      return <InputGroup className="mb-3" key={lang.code}>
        <input type="radio"
          name="lang"
          value={lang.code}
          onChange={formik.handleChange}
          defaultChecked={lang.code === globalLang}
        />
        <FormLabel>
          {t(lang.text)}
        </FormLabel>
      </InputGroup>;
    }), [globalLang]);

  const onRequestClose = () => setIsOpen(false);

  return <React.Fragment>
    <Button onClick={() => { setIsOpen(true); }}>
      {t('common.header.change_lang')}
    </Button>
    <ReactSlidingPane
      hideHeader
      isOpen={isOpen}
      width={breakpoint !== 'xs' ? '375px' : '100%'}
      shouldCloseOnEsc
      onRequestClose={onRequestClose}
      from={document.body.dir === 'ltr' ? 'right' : 'left'}
    >
      <form onSubmit={formik.handleSubmit}>
        <ResponsiveHeader xs="h3" className="text-capitalize mb-4">
          {t('common.header.change_lang')}
        </ResponsiveHeader>
        {renderLangsItems()}
        <Row className='justify-content-end'>
          <Col className="col-auto">
            <ButtonGroup className="non-adaptable">
              <Button
                variant="secondary"
                type="button"
                onClick={onRequestClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
              >
                {t('common.change')}
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </form>
    </ReactSlidingPane>
  </React.Fragment >;
};

LanguageSelectorSlideOver.propTypes = {
  availableLanguages: PropTypes.arrayOf(PropTypes.string)
};
LanguageSelectorSlideOver.defaultProps = {
  availableLanguages: ['en', 'fr', 'es', 'de', 'ja', 'pt', 'it', 'ar', 'hi']
};
export default React.memo(LanguageSelectorSlideOver);
